import { useEffect, useState } from "react";
import { api } from "../services/api";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import "./BuyerOrders.css";

export default function BuyerOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "My Orders | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Track your purchases on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
      processing: { color: "#3b82f6", bg: "#dbeafe", label: "Processing" },
      shipped: { color: "#8b5cf6", bg: "#ede9fe", label: "Shipped" },
      delivered: { color: "#10b981", bg: "#d1fae5", label: "Delivered" },
      cancelled: { color: "#ef4444", bg: "#fee2e2", label: "Cancelled" },
    };
    return (
      configs[status] || { color: "#6b7280", bg: "#f3f4f6", label: status }
    );
  };

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await api.getOrders();
        if (data.success) {
          setOrders(data.data.orders || []);
        } else {
          alert(data.message || "Failed to fetch orders");
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        alert("Failed to fetch orders");
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="bo-page">
      <Navbar />

      <div className="bo-container">
        {/* Page Header */}
        <div className="bo-header">
          <div className="bo-header-content">
            <h1>My Orders</h1>
            <p>Track and manage your purchases</p>
          </div>
          {orders.length > 0 && (
            <div className="bo-header-stats">
              <div className="bo-stat">
                <span className="bo-stat-number">{orders.length}</span>
                <span className="bo-stat-label">Total Orders</span>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="bo-loading">
            <div className="bo-spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bo-empty">
            <div className="bo-empty-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M16 11V7a4 4 0 0 0-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h2>No orders yet</h2>
            <p>
              Looks like you haven't made any purchases. Start exploring our
              products!
            </p>
            <button
              className="bo-btn bo-btn-primary"
              onClick={() => navigate("/products")}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="bo-orders-list">
            {orders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              return (
                <div
                  key={order._id}
                  className="bo-order-card"
                  onClick={() => navigate(`/orders/${order._id}`)}
                >
                  {/* Order Header */}
                  <div className="bo-order-header">
                    <div className="bo-order-info">
                      <span className="bo-order-id">
                        #{order._id.slice(-8).toUpperCase()}
                      </span>
                      <span className="bo-order-date">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        {formatDate(order.createdAt)}
                      </span>
                    </div>
                    <span
                      className="bo-status-badge"
                      style={{
                        color: statusConfig.color,
                        backgroundColor: statusConfig.bg,
                      }}
                    >
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Order Items Preview */}
                  <div className="bo-order-items">
                    <p className="bo-items-summary">
                      {order.items.length} item
                      {order.items.length !== 1 ? "s" : ""}
                    </p>
                    <p className="bo-items-list">
                      {order.items.map((item) => item.name).join(" • ")}
                    </p>
                  </div>

                  {/* Order Footer */}
                  <div className="bo-order-footer">
                    <div className="bo-order-total">
                      <span className="bo-total-label">Total</span>
                      <span className="bo-total-amount">
                        BND {order.totalPrice.toFixed(2)}
                      </span>
                    </div>
                    <div className="bo-view-details">
                      View Details
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
